import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { RefreshCw, BadgeCheck, Lock, Instagram, ChevronDown, Save, Loader2, CircleUser as UserCircle, Gauge, Home, Users, HelpCircle, LogOut, Sun, Moon, Target } from 'lucide-react';
import { BarChart3 } from 'lucide-react';
import { Sidebar } from './components/Sidebar/Sidebar';
import { AccountList } from './components/AccountList/AccountList';
import { Overview } from './pages/Overview/Overview';
import { getPlanBadgeStyles } from './utils/planStyles';
import { Targets } from './pages/Targets/Targets';
import { Plans } from './pages/Plans/Plans';
import { WelcomeDM } from './pages/WelcomeDM/WelcomeDM';
import { CloseFriends } from './pages/CloseFriends/CloseFriends';
import { Settings } from './pages/Settings/Settings';
import { Dashboard } from './pages/Dashboard/Dashboard';
import { Referral } from './pages/Referral/Referral';
import { Account } from './pages/Account/Account';
import { Auth } from './pages/Auth/Auth';
import { ForgotPassword } from './pages/Auth/ForgotPassword';
import { ResetPassword } from './pages/Auth/ResetPassword';
import { AddAccount } from './pages/AddAccount/AddAccount';
import { Onboarding } from './pages/Auth/Onboarding';
import { NotFound } from './pages/NotFound/NotFound';
import { Closing } from './pages/Closing/Closing';
import { Competitors } from './pages/Competitors/Competitors';
import { TikTok } from './pages/TikTok/TikTok';
import { MusicCharts } from './pages/MusicCharts/MusicCharts';
import { suggestedTargets, plans, dailyStats } from './data';
import { InstagramAccount, InstagramTarget, Profile } from './types';
import { translations } from './translations';
import { supabase } from './lib/supabase';
import { hikerApi } from './services/hikerApi';
import { imageService } from './services/imageService';
import { AddAccountModal } from './components/AccountList/AddAccountModal';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedItem, setSelectedItem] = useState(() => {
    const path = window.location.pathname;
    if (path === '/') return 'instagram';
    if (path === '/dashboard') return 'dashboard';
    if (path === '/referral') return 'parrainage';
    if (path === '/account') return 'account';
    if (path === '/closing') return 'closing';
    if (path === '/competitors') return 'competitors';
    if (path === '/music-charts') return 'music-charts';
    if (path === '/tiktok') return 'tiktok';
    return 'instagram';
  });

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAccountListOpen, setIsAccountListOpen] = useState(false);
  const [language, setLanguage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get('lang');
    if (urlLang && ['FR', 'EN', 'ES', 'RU'].includes(urlLang)) {
      localStorage.setItem('language', urlLang);
      return urlLang;
    }
    // Convert to uppercase and validate
    const storedLang = localStorage.getItem('language');
    if (storedLang) {
      const upperLang = storedLang.toUpperCase();
      if (['FR', 'EN', 'ES', 'RU'].includes(upperLang)) {
        return upperLang;
      }
    }
    // Also check URL parameter with case conversion
    if (urlLang) {
      const upperUrlLang = urlLang.toUpperCase();
      if (['FR', 'EN', 'ES', 'RU'].includes(upperUrlLang)) {
        localStorage.setItem('language', upperUrlLang);
        return upperUrlLang;
      }
    }
    return 'FR';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    const params = new URLSearchParams(window.location.search);
    params.set('lang', language);
    const newSearch = params.toString();
    const newPath = `${location.pathname}${newSearch ? '?' + newSearch : ''}`;
    navigate(newPath, { replace: true });
  }, [language]);

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const stored = localStorage.getItem('theme');
    return stored === 'dark';
  });

  const handleThemeChange = (isDark: boolean) => {
    setIsDarkMode(isDark);
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAccount, setSelectedAccount] = useState<InstagramAccount | null>(null);
  const [selectedTab, setSelectedTab] = useState(() => {
    return localStorage.getItem('selectedTab') || 'overview';
  });
  const [selectedTargets, setSelectedTargets] = useState<InstagramTarget[]>([]);
  const [accounts, setAccounts] = useState<InstagramAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingFollowers, setIsUpdatingFollowers] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshLimits, setRefreshLimits] = useState<{
    account_remaining: number;
    user_remaining: number;
    account_limit: number;
    user_limit: number;
  } | null>(null);
  const [session, setSession] = useState<any>(null);
  const [subscription, setSubscription] = useState<any>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCloser, setIsCloser] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  const t = translations[language as keyof typeof translations];

  const getConnectionStatusColor = (status: string | null) => {
    switch (status) {
      case 'connected':
        return 'text-emerald-500';
      case '2fa':
        return 'text-amber-500';
      case 'failed':
        return 'text-red-500';
      default:
        return isDarkMode ? 'text-gray-400' : 'text-gray-500';
    }
  };

  const getConnectionStatusText = (status: string | null) => {
    switch (status) {
      case 'connected':
        return t.connection_status_connected;
      case '2fa':
        return t.connection_status_2fa;
      case 'failed':
        return t.connection_status_failed;
      case 'pending':
        return t.connection_status_pending;
      default:
        return '';
    }
  };

  const handleUpdateConnectionStatus = async (newStatus: string | null) => {
    if (!selectedAccount) return;
    
    try {
      setIsUpdatingStatus(true);
      setError(null);

      const { error: updateError } = await supabase
        .from('instagram_accounts')
        .update({ 
          connection_status: newStatus,
          connection_updated_at: new Date().toISOString()
        })
        .eq('id', selectedAccount.id);

      if (updateError) throw updateError;

      await loadAccount(selectedAccount.id);
    } catch (err) {
      console.error('Error updating connection status:', err);
      setError('Failed to update connection status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleNavigation = (path: string, item: string) => {
    setSelectedItem(item);
    navigate(path);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('notfound') === 'true') {
      navigate('/404', { replace: true });
    }
  }, [location]);

  useEffect(() => {
    if (selectedAccount && (!selectedAccount.plan || selectedAccount.plan === 'Free') && selectedTab !== 'plans') {
      setSelectedTab('plans');
      localStorage.setItem('selectedTab', 'plans');
    }
  }, [selectedAccount, selectedTab]);

  const isTabLocked = (tab: string) => {
    if (!selectedAccount || !selectedAccount.plan) return true;

    if (selectedAccount.plan === 'Amplr Start') {
      return tab === 'welcome-dm' || tab === 'close-friends';
    }

    if (selectedAccount.plan === 'Amplr Max' || selectedAccount.plan === 'Amplr Pro') {
      return false;
    }

    return true;
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setIsLoading(false);
      if (!session) {
        navigate('/');
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setIsLoading(false);
      if (event === 'PASSWORD_RECOVERY') {
        navigate('/reset-password');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session?.user) {
      loadAccounts();
      loadSubscription();
      loadProfile();
    }
  }, [session]);

  const loadProfile = async () => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session?.user?.id)
        .single();

      if (error) throw error;
      setProfile(profile);
      setIsAdmin(profile.role === 'admin');
      setIsCloser(profile.role === 'closer');
    } catch (err) {
      console.error('Error loading profile:', err);
    }
  };

  const loadSubscription = async () => {
    try {
      const { data: customer } = await supabase
        .from('stripe_customers')
        .select('customer_id')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (customer) {
        const { data: subscriptions } = await supabase
          .from('stripe_subscriptions')
          .select('*')
          .eq('customer_id', customer.customer_id)
          .eq('status', 'active')
          .limit(1);

        setSubscription(subscriptions?.[0] || null);
      }
    } catch (err) {
      console.error('Error loading subscription:', err);
    }
  };

  const loadAccounts = async () => {
    try {
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', session.user.id)
        .single();

      if (profileError) throw profileError;

      let query = supabase
        .from('instagram_accounts')
        .select('*')
        .order('created_at', { ascending: false });

      if (userProfile.role === 'customer') {
        query = query.eq('user_id', session.user.id);
      } else if (userProfile.role === 'closer') {
        query = query.or(`user_id.eq.${session.user.id},visible_to_closers.eq.true`);
      }

      const { data: accounts, error } = await query;

      if (error) throw error;

      setAccounts(accounts || []);

      const storedAccountId = localStorage.getItem('selectedAccountId');
      
      if (userProfile.role === 'admin' || userProfile.role === 'closer') {
        const storedAccount = storedAccountId ? accounts?.find(a => a.id === storedAccountId) : null;
        setSelectedAccount(storedAccount || accounts?.[0] || null);
      } else {
        const connectedAccounts = accounts?.filter(a => a.connection_status === 'connected') || [];
        const storedAccount = storedAccountId ? connectedAccounts.find(a => a.id === storedAccountId) : null;
        setSelectedAccount(storedAccount || connectedAccounts[0] || null);
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
      setError('Failed to load accounts');
    }
  };

  const loadAccount = async (accountId: number) => {
    try {
      const { data: account, error } = await supabase
        .from('instagram_accounts')
        .select('*')
        .eq('id', accountId)
        .single();

      if (error) throw error;
      if (account) {
        setSelectedAccount(account);
        setAccounts(prevAccounts => 
          prevAccounts.map(a => a.id === account.id ? account : a)
        );
      }
    } catch (err) {
      console.error('Error loading account:', err);
      setError('Failed to load account');
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      localStorage.removeItem('selectedAccountId');
      localStorage.removeItem('selectedTab');
      navigate('/');
    } catch (err) {
      console.error('Error logging out:', err);
      setError('Failed to log out');
    }
  };

  const handleAddAccount = async (accountData: any) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        setError('User not authenticated');
        return;
      }

      let avatarUrl = accountData.avatar;
      if (avatarUrl && avatarUrl !== 'https://via.placeholder.com/150') {
        avatarUrl = await imageService.uploadAvatar(avatarUrl, accountData.username);
      }

      const { data, error } = await supabase
        .from('instagram_accounts')
        .insert({
          ...accountData,
          avatar: avatarUrl,
          user_id: userData.user.id,
          is_legacy_client: 'NEW'
        })
        .select()
        .single();

      if (error) throw error;

      await loadAccounts();
      
      if (data) {
        setSelectedAccount(data);
        localStorage.setItem('selectedAccountId', data.id);
        setSelectedTab('overview');
        localStorage.setItem('selectedTab', 'overview');
      }
      
      navigate('/');
    } catch (err) {
      console.error('Error adding account:', err);
      setError('Failed to add account');
    }
  };

  const checkRefreshLimits = async (accountId: number) => {
    try {
      const { data, error } = await supabase.rpc('check_refresh_limit', {
        p_user_id: session.user.id,
        p_account_id: accountId
      });

      if (error) throw error;
      
      setRefreshLimits(data);
      return data;
    } catch (err) {
      console.error('Error checking refresh limits:', err);
      return null;
    }
  };

  const incrementRefreshCount = async (accountId: number) => {
    try {
      const { data, error } = await supabase.rpc('increment_refresh_count', {
        p_user_id: session.user.id,
        p_account_id: accountId
      });

      if (error) throw error;
      
      setRefreshLimits(data);
      return data;
    } catch (err) {
      console.error('Error incrementing refresh count:', err);
      return null;
    }
  };

  const handleUpdateFollowers = async (accountId: number) => {
    try {
      setIsUpdatingFollowers(true);
      setError(null);

      // Call the secure refresh-limit edge function
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/refresh-limit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accountId }),
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to refresh account');
      }
      
      // Update refresh limits from the response
      if (result.limits) {
        setRefreshLimits(result.limits);
      }

      await loadAccount(accountId);

    } catch (err) {
      console.error('Error updating followers:', err);
      setError('Failed to update follower count');
    } finally {
      setIsUpdatingFollowers(false);
    }
  };

  // Load refresh limits when account changes
  useEffect(() => {
    if (selectedAccount && session?.user) {
      // Initial check of refresh limits
      checkRefreshLimits(selectedAccount.id).catch(console.error);
    }
  }, [selectedAccount, session?.user]);

  const handleSelectAccount = (account: InstagramAccount) => {
    setSelectedAccount(account);
    localStorage.setItem('selectedAccountId', account.id);
  };

  const handleSelectTab = (tab: string) => {
    setSelectedTab(tab);
    localStorage.setItem('selectedTab', tab);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (location.pathname === '/login' || location.pathname === '/forgot-password' || location.pathname === '/reset-password') {
    return (
      <Routes>
        <Route path="/login" element={
          <Auth 
            isDarkMode={isDarkMode} 
            language={language}
            onThemeChange={handleThemeChange}
            setLanguage={setLanguage}
          />
        } />
        <Route path="/forgot-password" element={
          <ForgotPassword 
            isDarkMode={isDarkMode} 
            language={language}
            onThemeChange={handleThemeChange}
            setLanguage={setLanguage}
          />
        } />
        <Route path="/reset-password" element={
          <ResetPassword 
            isDarkMode={isDarkMode} 
            language={language}
          />
        } />
      </Routes>
    );
  }

  if (!session?.user) {
    return (
      <Onboarding 
        isDarkMode={isDarkMode} 
        language={language}
        onThemeChange={handleThemeChange}
        setLanguage={setLanguage}
        plans={plans}
      />
    );
  }

  return (
    <div className={`min-h-screen flex ${isDarkMode ? 'dark bg-[#121212]' : 'bg-[#f0f2f5]'}`}>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-[60] bg-white dark:bg-[#1c1c1c] shadow-md">
        <div className="flex items-center justify-between px-3 h-16">
          <div className="flex items-center gap-3">
            <img 
               src="https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/logo/webclip.png"
               alt="Amplr AI"
               className="w-7 h-7 rounded-lg"
             />
             
            <button
              onClick={() => navigate('/add-account')}
              className="p-1.5 rounded-full bg-orange-500 text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14"></path>
                <path d="M12 5v14"></path>
              </svg>
            </button>
             
             <button
               onClick={() => {
                handleNavigation('/', 'instagram');
                setIsAccountListOpen(!isAccountListOpen);
                if (isSidebarOpen) setIsSidebarOpen(false);
               }}
               className={`p-2 rounded-lg ${isAccountListOpen ? 'bg-gray-100 dark:bg-[#2b2b2b]' : ''}`}
             >
               <Instagram size={18} className={isDarkMode ? 'text-gray-200' : 'text-gray-900'} />
             </button>
          </div>
          
          <div className="flex items-center gap-1">

            <button
              onClick={() => handleNavigation('/dashboard', 'dashboard')}
              className={`p-2 rounded-lg ${selectedItem === 'dashboard' ? 'bg-gray-100 dark:bg-[#2b2b2b]' : ''}`}
            >
              <RefreshCw size={18} className={isDarkMode ? 'text-gray-200' : 'text-gray-900'} />
            </button>
            
            {isAdmin && (
              <button
                onClick={() => handleNavigation('/competitors', 'competitors')}
                className={`p-2 rounded-lg ${selectedItem === 'competitors' ? 'bg-gray-100 dark:bg-[#2b2b2b]' : ''}`}
              >
                <Target size={18} className={isDarkMode ? 'text-gray-200' : 'text-gray-900'} />
              </button>
            )}
            {/* Referral button hidden on mobile */}
            <div className="hidden">
              <button
                onClick={() => handleNavigation('/referral', 'parrainage')}
                className={`p-2 rounded-lg ${selectedItem === 'parrainage' ? 'bg-gray-100 dark:bg-[#2b2b2b]' : ''}`}
              >
                <Users size={18} className={isDarkMode ? 'text-gray-200' : 'text-gray-900'} />
              </button>
            </div>
            
            <button
              onClick={() => handleNavigation('/account', 'account')}
              className={`p-2 rounded-lg ${selectedItem === 'account' ? 'bg-gray-100 dark:bg-[#2b2b2b]' : ''}`}
            >
              <UserCircle size={18} className={isDarkMode ? 'text-gray-200' : 'text-gray-900'} />
            </button>
            
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="p-2 rounded-lg"
            >
              {isDarkMode ? (
                <Sun size={18} className="text-gray-200" />
              ) : (
                <Moon size={18} className="text-gray-900" />
              )}
            </button>
            
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg"
            >
              <LogOut size={18} className={isDarkMode ? 'text-gray-200' : 'text-gray-900'} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Overlay */}
      {isAccountListOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => {
            setIsAccountListOpen(false);
          }}
        />
      )}

      {/* Desktop Sidebar - only visible on large screens */}
      <div className="hidden lg:relative lg:flex lg:block top-0 bottom-0 z-50 w-56">
        <Sidebar
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
          language={language}
          setLanguage={setLanguage}
          isDarkMode={isDarkMode}
          setIsDarkMode={handleThemeChange}
          onAddAccount={() => navigate('/add-account')}
          onLogout={handleLogout}
          onClose={() => {}}
          isAdmin={isAdmin}
        />
      </div>

      {error && (
        <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50">
          <p>{error}</p>
          <button 
            onClick={() => setError(null)}
            className="absolute top-1 right-2 text-red-700 hover:text-red-900"
          >
            ×
          </button>
        </div>
      )}

      <div className="flex-1 overflow-hidden">
        <Routes>
          <Route path="/add-account" element={
            <AddAccount
              isDarkMode={isDarkMode} 
              language={language}
              onAddAccount={handleAddAccount}
            />
          } />

          <Route path="/dashboard" element={
            <Dashboard isDarkMode={isDarkMode} language={language} />
          } />

          <Route path="/referral" element={
            <Referral isDarkMode={isDarkMode} language={language} />
          } />

          <Route path="/account" element={
            <Account isDarkMode={isDarkMode} language={language} />
          } />

          <Route path="/cockpit" element={
            isAdmin ? (
              <Competitors isDarkMode={isDarkMode} language={language} />
            ) : (
              <NotFound isDarkMode={isDarkMode} language={language} />
            )
          } />

          <Route path="/closing" element={
            isAdmin ? (
              <Closing isDarkMode={isDarkMode} language={language} />
            ) : (
              <NotFound isDarkMode={isDarkMode} language={language} />
            )
          } />

          <Route path="/competitors" element={
            (isAdmin || isCloser) ? (
              <Competitors isDarkMode={isDarkMode} language={language} />
            ) : (
              <NotFound isDarkMode={isDarkMode} language={language} />
            )
          } />

          <Route path="/tiktok" element={
            isAdmin ? (
              <TikTok isDarkMode={isDarkMode} language={language} />
            ) : (
              <NotFound isDarkMode={isDarkMode} language={language} />
            )
          } />

          <Route path="/music-charts" element={
            isAdmin ? (
              <MusicCharts isDarkMode={isDarkMode} language={language} />
            ) : (
              <NotFound isDarkMode={isDarkMode} language={language} />
            )
          } />
          <Route path="/" element={
            <div className="flex h-screen overflow-hidden">
              {/* Add Account Modal */}
              {showAddModal && (
                <AddAccountModal
                  onClose={() => setShowAddModal(false)}
                  onAdd={handleAddAccount}
                  isDarkMode={isDarkMode}
                  language={language}
                />
              )}
              
              <div className={`fixed lg:relative lg:block lg:w-72 inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 ${
                isAccountListOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0' 
              }`}>
                <AccountList
                  accounts={accounts}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  selectedAccount={selectedAccount}
                  setSelectedAccount={account => {
                    handleSelectAccount(account);
                    setIsAccountListOpen(false);
                  }}
                  setSelectedTab={handleSelectTab}
                  isDarkMode={isDarkMode}
                  language={language}
                  onShowAddModal={() => setShowAddModal(true)}
                  isCloser={isCloser}
                />
              </div>

              <div className="flex-1 overflow-y-auto p-4 md:p-8 pt-20 lg:pt-8">
                {selectedAccount && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-4">
                        <img
                          src={selectedAccount.profile_pic_url_hd || selectedAccount.avatar || 'https://via.placeholder.com/150'}
                          alt={selectedAccount.username}
                          className="w-12 h-12 rounded-full"
                          crossOrigin="anonymous"
                        />
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h2 className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                              {selectedAccount.name}
                            </h2>
                            {selectedAccount.is_verified && (
                              <BadgeCheck 
                                size={16} 
                                className="text-[#0095F6]" 
                                strokeWidth={2.5} 
                              />
                            )}
                            <button
                              onClick={() => handleUpdateFollowers(selectedAccount.id)}
                              className={`text-gray-400 hover:text-gray-600 transition-colors ${isUpdatingFollowers ? 'animate-spin' : ''}`}
                              disabled={isUpdatingFollowers || (refreshLimits && !refreshLimits.account_remaining)}
                              title={refreshLimits ? `${refreshLimits.account_remaining}/${refreshLimits.account_limit} ${t.refreshes_remaining}` : ''}
                            >
                              <div className="relative">
                                <RefreshCw size={14} strokeWidth={2} />
                                {refreshLimits && refreshLimits.account_remaining > 0 && (
                                  <span className="absolute -top-1 -right-1 text-[8px] font-semibold bg-gray-200 dark:bg-gray-700 rounded-full w-3 h-3 flex items-center justify-center">
                                    {refreshLimits.account_remaining}
                                  </span>
                                )}
                              </div>
                            </button>
                            {selectedAccount.plan && (
                              <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${getPlanBadgeStyles(selectedAccount.plan)}`}>
                                {selectedAccount.plan.replace('Amplr ', '')}
                              </span>
                            )}
                            {isAdmin ? (
                              <div className="relative inline-block">
                                <select
                                  value={selectedAccount.connection_status || 'null'}
                                  onChange={(e) => handleUpdateConnectionStatus(e.target.value === 'null' ? null : e.target.value)}
                                  disabled={isUpdatingStatus}
                                  className={`text-xs font-medium appearance-none pl-2 pr-6 py-0.5 rounded-full cursor-pointer ${
                                    getConnectionStatusColor(selectedAccount.connection_status)
                                  } ${
                                    isDarkMode ? 'bg-[#2b2b2b]' : 'bg-gray-100'
                                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                  <option value="null">Null</option>
                                  <option value="pending">{t.connection_status_pending}</option>
                                  <option value="connected">{t.connection_status_connected}</option>
                                  <option value="2fa">{t.connection_status_2fa}</option>
                                  <option value="failed">{t.connection_status_failed}</option>
                                </select>
                                <ChevronDown 
                                  size={12} 
                                  className={`absolute right-1.5 top-1/2 -translate-y-1/2 ${
                                    getConnectionStatusColor(selectedAccount.connection_status)
                                  }`} 
                                />
                              </div>
                            ) : (
                              selectedAccount.connection_status && selectedAccount.connection_status !== 'pending' && (
                                <span className={`text-xs font-medium ${getConnectionStatusColor(selectedAccount.connection_status)}`}>
                                  {getConnectionStatusText(selectedAccount.connection_status)}
                                </span>
                              )
                            )}
                            {isAdmin && (
                              <div className="flex items-center gap-2 ml-2">
                                <input
                                  type="checkbox"
                                  checked={selectedAccount.visible_to_closers || false}
                                  onChange={async (e) => {
                                    const newVisibility = e.target.checked;
                                    try {
                                      const { error } = await supabase
                                        .from('instagram_accounts')
                                        .update({ visible_to_closers: newVisibility })
                                        .eq('id', selectedAccount.id);

                                      if (error) throw error;

                                      // Update local state
                                      setSelectedAccount({
                                        ...selectedAccount,
                                        visible_to_closers: newVisibility
                                      });
                                    } catch (error) {
                                      console.error('Error updating visibility:', error);
                                      setError('Failed to update visibility');
                                    }
                                  }}
                                  className="w-3 h-3 text-orange-500 rounded focus:ring-orange-500"
                                />
                                <span className={`text-xs ${
                                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                  Closer
                                </span>
                              </div>
                            )}
                          </div>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {selectedAccount.username}
                          </p>
                          {selectedAccount.biography && (
                            <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {selectedAccount.biography.split('\n').map((line, index) => (
                                <span key={index}>
                                  {line}
                                  {index < selectedAccount.biography.split('\n').length - 1 && <br />}
                                </span>
                              ))}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="border-b border-gray-200 dark:border-gray-800 overflow-x-auto">
                      <nav className="flex gap-6 whitespace-nowrap">
                        <button
                          onClick={() => !isTabLocked('overview') && handleSelectTab('overview')}
                          className={`pb-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                            selectedTab === 'overview'
                              ? 'border-orange-500 text-orange-500'
                              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                          } ${isTabLocked('overview') ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {isTabLocked('overview') && <Lock size={12} className="mr-1" />}
                          {t.overview}
                        </button>
                        <button
                          onClick={() => !isTabLocked('targets') && handleSelectTab('targets')}
                          className={`pb-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                            selectedTab === 'targets'
                              ? 'border-orange-500 text-orange-500'
                              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                          } ${isTabLocked('targets') ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {isTabLocked('targets') && <Lock size={12} className="mr-1" />}
                          {t.targets}
                        </button>
                        <button
                          onClick={() => !isTabLocked('welcome-dm') && handleSelectTab('welcome-dm')}
                          className={`pb-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                            selectedTab === 'welcome-dm'
                              ? 'border-orange-500 text-orange-500'
                              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                          } ${isTabLocked('welcome-dm') ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {isTabLocked('welcome-dm') && <Lock size={12} className="mr-1" />}
                          {t.welcome_dm_tab}
                        </button>
                        <button
                          onClick={() => !isTabLocked('close-friends') && handleSelectTab('close-friends')}
                          className={`pb-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                            selectedTab === 'close-friends'
                              ? 'border-orange-500 text-orange-500'
                              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                          }  ${isTabLocked('close-friends') ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {isTabLocked('close-friends') && <Lock size={12} className="mr-1" />}
                          {t.close_friends}
                        </button>
                        <button
                          onClick={() => handleSelectTab('plans')}
                          className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
                            selectedTab === 'plans'
                              ? 'border-orange-500 text-orange-500'
                              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                          }`}
                        >
                          {t.plans}
                        </button>
                        <button
                          onClick={() => !isTabLocked('settings') && handleSelectTab('settings')}
                          className={`pb-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                            selectedTab === 'settings'
                              ? 'border-orange-500 text-orange-500'
                              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                          } ${isTabLocked('settings') ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {isTabLocked('settings') && <Lock size={12} className="mr-1" />}
                          {t.settings}
                        </button>
                      </nav>
                    </div>

                    <div className="pt-4">
                      {selectedTab === 'overview' && !isTabLocked('overview') && (
                        <Overview
                          account={selectedAccount}
                          isDarkMode={isDarkMode}
                          language={language}
                        />
                      )}
                      {selectedTab === 'targets' && !isTabLocked('targets') && (
                        <Targets
                          account={selectedAccount}
                          suggestedTargets={suggestedTargets}
                          selectedTargets={selectedTargets}
                          setSelectedTargets={setSelectedTargets}
                          isDarkMode={isDarkMode}
                          language={language}
                        />
                      )}
                      {selectedTab === 'plans' && (
                        <Plans
                          account={selectedAccount}
                          plans={plans}
                          isDarkMode={isDarkMode}
                          language={language}
                          onAccountDeleted={loadAccounts}
                        />
                      )}
                      {selectedTab === 'welcome-dm' && !isTabLocked('welcome-dm') && (
                        <WelcomeDM
                          account={selectedAccount}
                          isDarkMode={isDarkMode}
                          language={language}
                          onAccountUpdated={() => loadAccount(selectedAccount.id)}
                        />
                      )}
                      {selectedTab === 'close-friends' && !isTabLocked('close-friends') && (
                        <CloseFriends
                          account={selectedAccount}
                          isDarkMode={isDarkMode}
                          language={language}
                        />
                      )}
                      {selectedTab === 'settings' && !isTabLocked('settings') && (
                        <Settings
                          account={selectedAccount}
                          isDarkMode={isDarkMode}
                          language={language}
                          onAccountDeleted={loadAccounts}
                        />
                      )}
                    </div>
                  </div>
                )}
                {!selectedAccount && (
                  <div className="text-center mt-12">
                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                      {t.select_account}
                    </p>
                  </div>
                )}
              </div>
            </div>
          } />

          <Route path="/404" element={
            <NotFound isDarkMode={isDarkMode} language={language} />
          } />
          
          <Route path="*" element={
            <NotFound isDarkMode={isDarkMode} language={language} />
          } />
        </Routes>
      </div>
    </div>
  );
}

export default App;